import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderToStaticMarkup } from 'react-dom/server';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WysiwygEditor from '../components/WysiwygEditor';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import '../styles.css';
import './Admin.css';

const ARTICLE_TYPES = [
  { value: 'blog', label: 'Blog' },
  { value: 'journal', label: 'Journal' },
  { value: 'sermon', label: 'Sermon' },
  { value: 'article', label: 'Article' },
];

const DEFAULT_JOURNAL_CATEGORIES = [
  { value: 'society', label: 'Society' },
  { value: 'politics', label: 'Politics' },
  { value: 'culture', label: 'Culture' },
  { value: 'tech', label: 'Tech' },
  { value: 'science', label: 'Science' },
  { value: 'bible', label: 'Bible' },
  { value: 'theology', label: 'Theology' },
  { value: 'philosophy', label: 'Philosophy' },
  { value: 'cult', label: 'Cult' },
  { value: 'opinion', label: 'Opinion' },
  { value: 'finance', label: 'Finance' },
  { value: 'family', label: 'Family' },
  { value: 'life', label: 'Life' },
];

const TEMPLATE_LIBRARY = {
  default: [
    {
      id: 'default-basic',
      label: '基本テンプレート',
      body: `# {{TITLE}}

*{{DATE}}*

[{{AUTHOR}}]

![](/static/images/i4.jpg)

## リード

ここに導入文を書いてください。

---

本文をここに記述します。
`,
    },
  ],
  blog: [
    {
      id: 'blog-highlight',
      label: 'ブログ: ハイライト',
      body: `# {{TITLE}}

*{{DATE}}*

[{{AUTHOR}}]

![](/static/images/i4.jpg)

出典：

https://youtube.com/@asmrchurch
`,
    },
  ],
  journal: [
    {
      id: 'journal-article',
      label: '新聞: 記事',
      body: `# {{TITLE}}

*{{DATE}}*

[{{AUTHOR}}]

![](/static/images/i4.jpg)

導入文を記載します。

詳細を記載します。
`,
    },
  ],
  sermon: [
    {
      id: 'sermon-outline',
      label: '説教: アウトライン',
      body: `# {{TITLE}}

![Alt text](/static/images/journalh.jpg)

*{{DATE}}*

[claude]

*出典:*
- 

https://youtube.com/@asmrchurch
`,
    },
  ],
};

function resolveTemplate(type, templateId) {
  const templates = TEMPLATE_LIBRARY[type] || [];
  if (templateId) {
    const selected = templates.find((tpl) => tpl.id === templateId)
      || TEMPLATE_LIBRARY.default.find((tpl) => tpl.id === templateId);
    if (selected) {
      return selected;
    }
  }
  return templates[0] || TEMPLATE_LIBRARY.default[0];
}

function buildTemplateBody(type, templateId, overrides = {}) {
  const template = resolveTemplate(type, templateId);
  const base = template ? template.body : '';
  const today = new Date().toISOString().split('T')[0];
  const title = overrides.title ?? 'タイトル';
  const author = overrides.author ?? 'sugano';
  const date = overrides.date ?? today;
  return base
    .replace(/{{DATE}}/g, date)
    .replace(/{{TITLE}}/g, title)
    .replace(/{{AUTHOR}}/g, author);
}

function extractFirstImage(markdown) {
  if (!markdown) {
    return '';
  }
  const match = markdown.match(/!\[[^\]]*\]\(([^)]+)\)/);
  return match && match[1] ? match[1] : '';
}

function generateLegacyId(type) {
  return `${type}-${Date.now()}`;
}

function applyTitleToBody(body, title) {
  const safeTitle = title?.trim() || '';
  if (!safeTitle) {
    return body || '';
  }
  const headingLine = `# ${safeTitle}`;
  if (!body) {
    return `${headingLine}\n`;
  }
  const [firstLine, ...rest] = body.split('\n');
  if (firstLine.startsWith('#')) {
    return [headingLine, ...rest].join('\n');
  }
  return [headingLine, firstLine, ...rest].join('\n');
}

const DEFAULT_FORM = {
  legacyId: '',
  title: '',
  body: '',
  excerpt: '',
  heroImage: '',
  category: '',
  tags: '',
  author: 'sugano',
  metadataDescription: '',
  metadataImage: '',
  metadataCanonicalPath: '',
  createdAt: '',
  status: 'draft',
};

function createEmptyForm(selectedType) {
  return {
    ...DEFAULT_FORM,
    legacyId: generateLegacyId(selectedType),
    metadataCanonicalPath: `/${selectedType}/`,
    body: buildTemplateBody(selectedType, null, { author: 'sugano' }),
  };
}

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedType, setSelectedType] = useState('journal');
  const [articles, setArticles] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [editingLegacyId, setEditingLegacyId] = useState('');
  const [formData, setFormData] = useState(() => createEmptyForm('journal'));
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('default-basic');
  const [journalCategories, setJournalCategories] = useState(DEFAULT_JOURNAL_CATEGORIES);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [pendingSelection, setPendingSelection] = useState(null);
  const [searchParams] = useSearchParams();
  const [editorMode, setEditorMode] = useState('wysiwyg');
  const [addingJournalCategory, setAddingJournalCategory] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [activeAdminTab, setActiveAdminTab] = useState('content');

  const NEW_CATEGORY_OPTION = '__new__';

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('/api/admin/session', { credentials: 'include' });
        const data = await response.json();
        setIsAuthenticated(Boolean(data.authenticated));
      } catch (error) {
        console.error('Failed to verify session', error);
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (!statusMessage) {
      return;
    }
    setToastMessage(statusMessage);
    setToastType(statusType);
    const timer = setTimeout(() => {
      setToastMessage('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [statusMessage, statusType]);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    const idParam = searchParams.get('id');
    if (typeParam) {
      const validType = ARTICLE_TYPES.find((item) => item.value === typeParam);
      if (validType) {
        setSelectedType(typeParam);
      }
    }
    if (typeParam && idParam) {
      setPendingSelection({ type: typeParam, id: idParam });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    loadArticles(selectedType);
    if (selectedType === 'journal') {
      loadJournalCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedType]);

  const loadJournalCategories = useCallback(async () => {
    try {
      const response = await fetch(`/api/admin/categories?type=journal`, {
        credentials: 'include',
      });
      if (!response.ok) {
        console.warn('Failed to load categories from database, using defaults');
        return;
      }
      const data = await response.json();
      if (data.categories && Array.isArray(data.categories)) {
        // Merge database categories with default ones, removing duplicates
        const allCategories = [...DEFAULT_JOURNAL_CATEGORIES];
        data.categories.forEach((dbCategory) => {
          if (!allCategories.some((cat) => cat.value === dbCategory.value)) {
            allCategories.push(dbCategory);
          }
        });
        setJournalCategories(allCategories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  const templateOptions = useMemo(() => {
    const defaults = TEMPLATE_LIBRARY.default || [];
    const specific = TEMPLATE_LIBRARY[selectedType] || [];
    return [...defaults, ...specific];
  }, [selectedType]);

  useEffect(() => {
    if (!templateOptions.length) {
      setSelectedTemplateId('');
      return;
    }
    const exists = templateOptions.some((tpl) => tpl.id === selectedTemplateId);
    if (!exists) {
      setSelectedTemplateId(templateOptions[0].id);
    }
  }, [templateOptions, selectedTemplateId]);

  const loadArticles = useCallback(async (type) => {
    if (!type) {
      return;
    }
    setIsLoadingList(true);
    try {
      const response = await fetch(`/api/admin/articles?type=${type}&page=1&pageSize=200`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to load articles');
      }
      const data = await response.json();
      setArticles(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      console.error(error);
      setArticles([]);
      setStatusType('error');
      setStatusMessage('記事一覧の取得に失敗しました。');
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
    if (loginError) {
      setLoginError('');
    }
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm),
      });
      if (!response.ok) {
        throw new Error('fail');
      }
      const data = await response.json();
      if (data.authenticated) {
        setIsAuthenticated(true);
      setFormData(createEmptyForm(selectedType));
      loadArticles(selectedType);
    } else {
      setLoginError('ログインに失敗しました');
    }
  } catch (error) {
      console.error('Login failed', error);
      setLoginError('ログインに失敗しました');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      setIsAuthenticated(false);
      setEditingLegacyId('');
      setFormData(createEmptyForm(selectedType));
      setActiveAdminTab('content');
    }
  };

  const handleSelectType = (event) => {
    const value = event.target.value;
    setSelectedType(value);
    setArticles([]);
    setEditingLegacyId('');
    setFormData(createEmptyForm(value));
    if (value !== 'journal') {
      setAddingJournalCategory(false);
      setNewCategoryInput('');
    }
  };

  useEffect(() => {
    if (selectedType !== 'journal') {
      return;
    }
    setFormData((prev) => {
      if (prev.category) {
        return prev;
      }
      const defaultOption = journalCategories[0];
      return defaultOption ? { ...prev, category: defaultOption.value } : prev;
    });
  }, [selectedType, journalCategories]);

  const renderSelectedTemplate = () => buildTemplateBody(selectedType, selectedTemplateId, {
    title: formData.title,
    author: formData.author || 'sugano',
  });

  const openPreviewWindow = () => {
    const win = window.open('', '_blank');
    if (!win) {
      setStatusType('error');
      setStatusMessage('ポップアップがブロックされました。');
      return;
    }
    const contentHtml = renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {formData.body || ''}
      </ReactMarkdown>,
    );
    const doc = `<!doctype html>
      <html lang="ja">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${formData.title || 'プレビュー'} - ASMRキリスト教会</title>
          <style>
            body { font-family: 'Hiragino Sans', 'Noto Sans JP', sans-serif; margin: 40px auto; max-width: 740px; line-height: 1.7; padding: 0 16px; }
            img { max-width: 100%; height: auto; }
            pre { background: #111; color: #fff; padding: 12px; overflow-x: auto; }
            blockquote { border-left: 4px solid #ccc; margin: 0; padding-left: 16px; color: #555; }
          </style>
        </head>
        <body>
          <article>${contentHtml}</article>
        </body>
      </html>`;
    win.document.write(doc);
    win.document.close();
  };

  const handleApplyTemplate = () => {
    setFormData((prev) => ({
      ...prev,
      body: renderSelectedTemplate(),
    }));
    setStatusType('success');
    setStatusMessage('テンプレートを挿入しました。');
  };

const handleListItemClick = useCallback(async (legacyId) => {
    setStatusMessage('');
    try {
      const response = await fetch(`/api/admin/articles/${selectedType}/${legacyId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('記事を取得できませんでした');
      }
      const data = await response.json();
      setEditingLegacyId(data.legacyId);
      setFormData({
        legacyId: data.legacyId,
        title: data.title || '',
        body: data.body || '',
        excerpt: data.excerpt || '',
        heroImage: data.heroImage || '',
        category: data.category || '',
        tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
        author: data.author || 'sugano',
        metadataDescription: data.metadata?.description || '',
        metadataImage: data.metadata?.image || '',
        metadataCanonicalPath: data.metadata?.canonicalPath || `/${selectedType}/${data.legacyId}`,
        createdAt: data.createdAt ? new Date(data.createdAt).toISOString().slice(0, 16) : '',
        status: data.status || 'draft',
      });
      setAddingJournalCategory(false);
      setNewCategoryInput('');
      if (selectedType === 'journal' && data.category) {
        setJournalCategories((prev) => {
          if (prev.some((item) => item.value === data.category)) {
            return prev;
          }
          return [...prev, { value: data.category, label: data.category }];
        });
      }
    } catch (error) {
      console.error(error);
      setStatusType('error');
      setStatusMessage('記事の読み込みに失敗しました。');
    }
  }, [selectedType]);

  useEffect(() => {
    if (!isAuthenticated || !pendingSelection) {
      return;
    }
    if (pendingSelection.type !== selectedType) {
      return;
    }
    handleListItemClick(pendingSelection.id);
    setPendingSelection(null);
  }, [isAuthenticated, pendingSelection, selectedType, handleListItemClick]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    if (name === 'title') {
      setFormData((prev) => ({
        ...prev,
        title: value,
        body: applyTitleToBody(prev.body, value),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBodyChange = (markdown) => {
    setFormData((prev) => ({ ...prev, body: markdown }));
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleInlineImageUpload = async (file) => {
    if (!file) {
      return null;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const response = await fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filename: file.name, data: dataUrl }),
      });
      if (!response.ok) {
        throw new Error('upload failed');
      }
      const payload = await response.json();
      setStatusType('success');
      setStatusMessage('画像を挿入しました。');
      return payload.path;
    } catch (error) {
      console.error(error);
      setStatusType('error');
      setStatusMessage('画像アップロードに失敗しました。');
      return null;
    }
  };

  const handleAddJournalCategory = () => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed) {
      return;
    }
    setJournalCategories((prev) => {
      if (prev.some((item) => item.value === trimmed)) {
        return prev;
      }
      return [...prev, { value: trimmed, label: trimmed }];
    });
    setFormData((prev) => ({ ...prev, category: trimmed }));
    setNewCategoryInput('');
    setAddingJournalCategory(false);
    setStatusType('success');
    setStatusMessage('カテゴリを追加しました。');
  };

  const handleSave = useCallback(async (saveStatus = null) => {
    if (!formData.title || !formData.body) {
      setStatusType('error');
      setStatusMessage('タイトルと本文は必須です。');
      return;
    }
    setIsSaving(true);
    setStatusMessage('');
    try {
      const assignedId = (editingLegacyId || formData.legacyId || '').trim() || String(Date.now());
      const firstImage = extractFirstImage(formData.body);
      const heroImage = firstImage || formData.heroImage || null;
      const metadataImage = firstImage || formData.metadataImage || null;
      const statusToSave = saveStatus !== null ? saveStatus : (formData.status || 'draft');
      const payload = {
        type: selectedType,
        legacyId: assignedId,
        slug: `${selectedType}-${assignedId}`,
        title: formData.title,
        body: formData.body,
        excerpt: formData.excerpt,
        heroImage,
        category: formData.category,
        author: formData.author,
        metadata: {
          description: formData.metadataDescription,
          image: metadataImage,
          canonicalPath: formData.metadataCanonicalPath || `/${selectedType}/${formData.legacyId}`,
        },
        tags: formData.tags,
        createdAt: formData.createdAt || undefined,
        status: statusToSave,
      };
      const method = editingLegacyId ? 'PUT' : 'POST';
      const endpoint = editingLegacyId
        ? `/api/admin/articles/${selectedType}/${editingLegacyId}`
        : '/api/admin/articles';
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('保存に失敗しました');
      }
      const saved = await response.json();
      setEditingLegacyId(saved.legacyId);
      const previousStatus = formData.status;
      setFormData((prev) => ({ ...prev, legacyId: saved.legacyId, status: statusToSave }));
      setStatusType('success');
      if (statusToSave === 'draft' && previousStatus === 'published') {
        setStatusMessage('下書きに戻しました。');
      } else {
        setStatusMessage(statusToSave === 'draft' ? '下書きを保存しました。' : '公開しました。');
      }
      loadArticles(selectedType);
    } catch (error) {
      console.error(error);
      setStatusType('error');
      setStatusMessage('保存に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, editingLegacyId, selectedType]);

  // Auto-save draft every 30 seconds (only for new articles or draft articles, never for published)
  useEffect(() => {
    if (!isAuthenticated || isSaving) {
      return;
    }

    // Don't auto-save published articles
    if (formData.status === 'published') {
      return;
    }

    // Only auto-save if title and body exist
    if (!formData.title || !formData.body) {
      return;
    }

    // Set up auto-save timer
    const autoSaveTimer = setTimeout(() => {
      // Auto-save as draft
      handleSave('draft');
    }, 30000); // 30 seconds

    // Clear timer on cleanup
    return () => clearTimeout(autoSaveTimer);
  }, [formData.title, formData.body, formData.excerpt, formData.category, formData.tags, formData.status, isAuthenticated, isSaving, handleSave]);

  const handleDelete = async () => {
    if (!editingLegacyId) {
      return;
    }
    const confirmed = window.confirm('この記事を削除しますか？');
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/articles/${selectedType}/${editingLegacyId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('削除に失敗しました');
      }
      setStatusType('success');
      setStatusMessage('削除しました。');
      setEditingLegacyId('');
      setFormData(createEmptyForm(selectedType));
      loadArticles(selectedType);
    } catch (error) {
      console.error(error);
      setStatusType('error');
      setStatusMessage('削除に失敗しました。');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderLoginForm = () => (
    <div className="admin-login-panel">
      <h2>管理者ログイン</h2>
      <form onSubmit={handleLoginSubmit}>
        <label htmlFor="email">
          メールアドレス
          <input
            id="email"
            name="email"
            type="email"
            value={loginForm.email}
            onChange={handleLoginChange}
            autoComplete="email"
          />
        </label>
        <label htmlFor="password">
          パスワード
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={loginForm.password}
              onChange={handleLoginChange}
              autoComplete="current-password"
              style={{ paddingRight: '40px', flex: 1 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
        </label>
        {loginError && <p className="admin-error">{loginError}</p>}
        <button type="submit" className="admin-primary">ログイン</button>
        <p className="admin-login-help">
          パスワードを忘れた場合は
          {' '}
          <Link to="/password-reset">こちら</Link>
          {' '}
          から再設定してください。
        </p>
      </form>
    </div>
  );

  const renderContentManager = () => (
    <>
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <label>
            種別
            <select value={selectedType} onChange={handleSelectType}>
              {ARTICLE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => loadArticles(selectedType)} disabled={isLoadingList}>
            {isLoadingList ? '更新中...' : '再読み込み'}
          </button>
          <button type="button" onClick={() => { setEditingLegacyId(''); setFormData(createEmptyForm(selectedType)); }}>
            新規作成
          </button>
        </div>
      </div>
      {templateOptions.length > 0 && (
        <div className="admin-template-bar">
          <label>
            テンプレート
            <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
              {templateOptions.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{tpl.label}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={handleApplyTemplate}>
            テンプレート適用
          </button>
        </div>
      )}
      <div className="admin-columns">
        <div className="admin-list-panel">
          <h3>記事一覧</h3>
          <div className="admin-list">
            {articles.map((article) => (
              <button
                type="button"
                key={article.id}
                className={`admin-list-item ${editingLegacyId === article.legacyId ? 'active' : ''}`}
                onClick={() => handleListItemClick(article.legacyId)}
              >
                <span className="admin-list-title">
                  {article.title || '(無題)'}
                  {article.status === 'draft' && <span className="admin-draft-badge"><span className="admin-draft-icon">💾</span>下書き</span>}
                </span>
                <span className="admin-list-meta">#{article.legacyId}</span>
              </button>
            ))}
            {!articles.length && (<p className="admin-empty">データがありません。</p>)}
          </div>
        </div>
        <div className="admin-editor-panel">
          <div className="admin-editor-form">
            <div className="admin-form-row">
              <label>
                ID
                <input
                  name="legacyId"
                  value={formData.legacyId}
                  readOnly
                  className="read-only"
                />
              </label>
              <label>
                タイトル
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleFieldChange}
                  placeholder="タイトル"
                />
              </label>
            </div>
            <div className="admin-form-row">
              {selectedType === 'journal' ? (
                <>
                  <label>
                    カテゴリ
                    <select
                      name="category"
                      value={addingJournalCategory ? NEW_CATEGORY_OPTION : (formData.category || '')}
                      onChange={(event) => {
                        const { value } = event.target;
                        if (value === NEW_CATEGORY_OPTION) {
                          setAddingJournalCategory(true);
                          setFormData((prev) => ({ ...prev, category: '' }));
                        } else {
                          setAddingJournalCategory(false);
                          handleFieldChange({ target: { name: 'category', value } });
                        }
                      }}
                    >
                      <option value="">未選択</option>
                      <option value={NEW_CATEGORY_OPTION}>新規カテゴリ</option>
                      {journalCategories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {addingJournalCategory && (
                    <label>
                      新規カテゴリ名
                      <div className="admin-input-with-button">
                        <input
                          value={newCategoryInput}
                          onChange={(event) => setNewCategoryInput(event.target.value)}
                          placeholder="カテゴリ名"
                        />
                        <button type="button" onClick={handleAddJournalCategory}>追加</button>
                      </div>
                    </label>
                  )}
                </>
              ) : (
                <label>
                  カテゴリ
                  <input name="category" value={formData.category} onChange={handleFieldChange} />
                </label>
              )}
              <label>
                著者
                <input name="author" value={formData.author} onChange={handleFieldChange} />
              </label>
            </div>
            <div className="admin-form-row">
              <label>
                タグ (カンマ区切り)
                <input name="tags" value={formData.tags} onChange={handleFieldChange} />
              </label>
            </div>
            <div className="admin-form-row">
              <label>
                抜粋
                <input name="excerpt" value={formData.excerpt} onChange={handleFieldChange} />
              </label>
              <label>
                公開日時
                <input name="createdAt" type="datetime-local" value={formData.createdAt} onChange={handleFieldChange} />
              </label>
            </div>
            <div className="admin-field-group">
              <div className="admin-editor-header">
                <span className="admin-field-label">本文</span>
                <div className="editor-tabs">
                  <button
                    type="button"
                    className={editorMode === 'wysiwyg' ? 'active' : ''}
                    onClick={() => setEditorMode('wysiwyg')}
                  >
                    WYSIWYG
                  </button>
                  <button
                    type="button"
                    className={editorMode === 'markdown' ? 'active' : ''}
                    onClick={() => setEditorMode('markdown')}
                  >
                    Markdown
                  </button>
                </div>
              </div>
              {editorMode === 'wysiwyg' ? (
                <WysiwygEditor
                  value={formData.body}
                  onChange={handleBodyChange}
                  onImageUpload={handleInlineImageUpload}
                />
              ) : (
                <textarea
                  className="admin-editor"
                  rows={20}
                  value={formData.body}
                  onChange={(event) => handleBodyChange(event.target.value)}
                />
              )}
            </div>
            <div className="admin-form-row">
              <label>
                メタ説明
                <input name="metadataDescription" value={formData.metadataDescription} onChange={handleFieldChange} />
              </label>
            </div>
            <label>
              カノニカルパス
              <input name="metadataCanonicalPath" value={formData.metadataCanonicalPath} onChange={handleFieldChange} />
            </label>
            <div className="admin-actions">
              <button type="button" onClick={openPreviewWindow}>
                プレビュー
              </button>
              {formData.status === 'published' && formData.legacyId && (
                <a
                  href={`/${selectedType}/${formData.legacyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-view-link"
                  title="公開ページを表示"
                >
                  公開ページを表示 ↗
                </a>
              )}
              {formData.status === 'published' ? (
                <button type="button" className="admin-secondary" onClick={() => handleSave('draft')} disabled={isSaving}>
                  {isSaving ? '保存中...' : '下書きに戻す'}
                </button>
              ) : (
                <button type="button" className="admin-secondary" onClick={() => handleSave('draft')} disabled={isSaving}>
                  {isSaving ? '保存中...' : '下書き保存'}
                </button>
              )}
              <button type="button" className="admin-primary" onClick={() => handleSave('published')} disabled={isSaving}>
                {isSaving ? '保存中...' : '公開'}
              </button>
              <button type="button" className="admin-danger" onClick={handleDelete} disabled={!editingLegacyId || isDeleting}>
                {isDeleting ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <div className="admin-top-bar">
        <div className="admin-tabs">
          <button
            type="button"
            className={`admin-tab-button ${activeAdminTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveAdminTab('content')}
          >
            コンテンツ管理
          </button>
          <button
            type="button"
            className={`admin-tab-button ${activeAdminTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveAdminTab('analytics')}
          >
            アクセス解析
          </button>
        </div>
        <button type="button" className="admin-text" onClick={handleLogout}>ログアウト</button>
      </div>
      {activeAdminTab === 'analytics' ? <AnalyticsDashboard /> : renderContentManager()}
    </div>
  );

  return (
    <div className="admin-page" data-color-mode="light">
      <Header title="管理画面 - ASMRキリスト教会" description="ASMRキリスト教会 管理画面" />
      <div className="admin-wrapper">
        {!sessionChecked && <p>確認中...</p>}
        {sessionChecked && (isAuthenticated ? renderDashboard() : renderLoginForm())}
      </div>
      {toastMessage && (
        <div className={`admin-toast ${toastType === 'error' ? 'error' : 'success'}`}>
          {toastMessage}
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Admin;
